import type { AiVoiceRecord } from '@src/types.ts/aiVoiceRecord';

// DI19 three-tier resolution of the AI Voice a speaker should be dubbed with on
// a given run, for the run's target language:
//   1. an explicit run override (this session), if it's still one of the
//      speaker's voices;
//   2. the speaker's per-language pin for the target language, if it's still one
//      of the speaker's voices AND it speaks the target language;
//   3. the first of the speaker's ordered `ai_voices[]` that speaks the target
//      language.
//
// In the run kick-off popover the choice is scoped to the target language: the
// picker only offers voices that speak it, so the default must too. If none of
// the speaker's voices speak the language this returns `undefined` (the row then
// prompts to assign a language voice — DubRunVoiceMapper). A missing/null
// `languageId` (still resolving) falls back to the plain top voice.
//
// `languageId` is the DatoCMS `Language.id` (the key `languagePrefs` is keyed
// by), not the BCP-47 code — resolve it from the run's target language before
// calling.
/** Which rung of the ladder decided the voice, for the row to say so. */
export type RunVoiceTier = 'override' | 'pinned' | 'automatic';

export interface RunVoiceResolution {
  voiceId: string | undefined;
  /** Null when no voice resolves. */
  tier: RunVoiceTier | null;
}

/**
 * `resolveVoiceForRun` plus the reason: an override that merely repeats the
 * pin counts as the pin (nothing about this dub differs from the saved choice).
 */
export function explainVoiceForRun(params: {
  orderedVoices: AiVoiceRecord[];
  languagePrefs: Record<string, string>;
  languageId: string | null;
  runOverride?: string | null;
}): RunVoiceResolution {
  const { languagePrefs, languageId, runOverride } = params;
  const voiceId = resolveVoiceForRun(params);
  if (!voiceId) return { voiceId, tier: null };
  const pinned = languageId ? languagePrefs[languageId] : undefined;
  if (runOverride && voiceId === runOverride && voiceId !== pinned) {
    return { voiceId, tier: 'override' };
  }
  if (pinned && voiceId === pinned) return { voiceId, tier: 'pinned' };
  return { voiceId, tier: 'automatic' };
}

export function resolveVoiceForRun(params: {
  orderedVoices: AiVoiceRecord[];
  languagePrefs: Record<string, string>;
  languageId: string | null;
  runOverride?: string | null;
}): string | undefined {
  const { orderedVoices, languagePrefs, languageId, runOverride } = params;
  const isSpeakerVoice = (id: string | null | undefined): id is string =>
    !!id && orderedVoices.some(v => v.id === id);

  const speaksLanguage = (id: string): boolean =>
    !languageId ||
    (orderedVoices
      .find(v => v.id === id)
      ?.supportedLanguages?.some(l => l.id === languageId) ??
      false);

  // An explicit run override wins — but re-validate it still speaks the target
  // language (defense-in-depth: a voice's `supportedLanguages` can change after
  // selection, and this keeps the submitted map consistent with what the
  // language-scoped picker shows).
  if (isSpeakerVoice(runOverride) && speaksLanguage(runOverride))
    return runOverride;

  // Language still resolving: no language scoping possible yet.
  if (!languageId) return orderedVoices[0]?.id;

  const pinned = languagePrefs[languageId];
  if (isSpeakerVoice(pinned) && speaksLanguage(pinned)) return pinned;

  return orderedVoices.find(v => speaksLanguage(v.id))?.id;
}
